import { IYibaoOpening, IYibaoLeagalPerson, IYibaoLicense, IElectronicLeagal, IBeneficiary, EBankAccountType } from '../src/components';
import { EAgentLevel, EChannelStatus, EMerchantBusinessRole, EOfflineReceiptStatus, EPaymentChannel, EProductClass, EPublicCollectionStatus, EPublicOpenbankType, IPageParam } from './common';

export enum EYibaoSignType {
  /** 个体工商户 */
  INDIVIDUAL = 'INDIVIDUAL',
  /** 企业 */
  ENTERPRISE = 'ENTERPRISE',
}

export enum EYibaoStepKey {
  License = 'license',
  Legal = 'legal',
  AccOpening = 'accOpening',
  /** 分账资质证明 */
  AccQualification = 'accQualification',
  Submit = 'submit',
  Submited = 'submited',
}

export interface IYibaoShop {
  /** 是否选中 */
  selected?: boolean;
  /**
   * 平台类型
   * @description {@link EYibaoBusinessType}
   */
  businessType?: EYibaoBusinessType;
  /** 电商平台店铺ID */
  shopId?: string;
  /** 店铺名称 */
  shopName?: string;
  /** 店铺地址URL */
  shopAddress?: string;
  /** 公域平台店铺截图 */
  shopPicUrl?: string;
  /** 公域管理后台截图 */
  shopManagementPicUrl?: string;
  /** 30天内资金流水截图 */
  tradePicUrl?: string;
}

export interface IProductFee {
  /** 使用场景编码 */
  usageCode: string;
  /** 使用场景名称 */
  usageName: string;
  /** 产品编号 */
  productCode: string;
  /** 产品名称 */
  productName: string;
  /** 费率 */
  cost: number;
  /** 特代费率 */
  agentCost: number;
  /** 每日限额 */
  dailyLimit: number;
  /** 可用银行 */
  availableBank: string;
  /** 成本单位类型 1:百分比|2:元/笔|3：元/户 */
  costUnitType: 1 | 2 | 3;
  /** 产品说明 */
  productDescription?: string;
}

/** 易宝钱包基础信息 */
export interface IYibaoBaseInfo {
  /** 支付场景 */
  paymentScene?: string;
  /**
   * 分账场景
   * @description {@link EShareScene}
   */
  shareScene?: EShareScene;
  /** 公域节点显示？ */
  yeepayOnlinePublicCollectionIsOpen?: boolean;
  /** 线下节点显示？ */
  yeepayOfflineOfflineReceiptIsOpen?: boolean;
  /**
   * 通道产品费率
   * @description {@link IProductFee}
   */
  productFees?: Array<IProductFee>;
  /**
   * 通道状态
   * @description {@link EChannelStatus}
   */
  channelStatus?: EChannelStatus;
  /**
   * 易宝账户状态
   * @description {@link EYibaoAccountStatus}
   */
  merchantStatus?: EYibaoAccountStatus;
  /** 备注 */
  merchantStatusRemark?: string;
  /**
   * 公域收款开通银行
   * @description {@link EPublicOpenbankType}
   */
  publicCollectionOpenbank?: EPublicOpenbankType;
  /** 开立银行账户短信认证联系人姓名（只针对新网银行） */
  bankOpeningSmsAuthContactMobile?: string;
  /** 开立银行账户短信认证联系人手机号（只针对新网银行） */
  bankOpeningSmsAuthContactName?: string;
  /**
   * 公域收款开通状态
   * @description {@link EPublicCollectionStatus}
   */
  publicCollectionStatus?: EPublicCollectionStatus;
  /** 公域收款开通状态说明 */
  publicCollectionStatusRemark?: string;
  /**
   * 线下收单开通状态
   * * @description {@link EOfflineReceiptStatus}
   */
  offlineReceiptStatus?: EOfflineReceiptStatus;
  /** 线下收单开通状态说明 */
  offlineReceiptStatusRemark?: string;
  /**
   * 入网商户业务角色
   * @description {@link EMerchantBusinessRole}
   */
  merchantBusinessRole?: EMerchantBusinessRole;
  /** 易宝商户主键 */
  id?: number;
  /** 开户名称 */
  name?: string;
  /** 易宝入网商编 */
  merchantNo?: string;
  /**
   * 入网申请状态
   * @description {@link EYibaoApplyStatus}
   */
  applicationStatus?: EYibaoApplyStatus;
  /** 待协议签署外链 */
  agreementSignUrl?: string;
  /**
   * 商户开通的产品
   * @description {@link IYibaoOpeningProduct}
   */
  openingProducts?: IYibaoOpeningProduct[];
  /**
   * 代理商端 - 易宝钱包钱款
   * * @description {@link ICompanyBalance}
   */
  companyBalance?: ICompanyBalance;
  /**
   * 企业端 - 易宝钱包钱款
   * * @description {@link IAgentBalance}
   */
  agentBalance?: IAgentBalance;
}

export interface IAgentBalance {
  /** 待结算金额 */
  unsettledAmount?: number;
  /** 可提现金额 */
  withdrawableAmount?: number;
  /** 提现中金额 */
  withdrawingAmount?: number;
  /** 退款金额 */
  refundAmount?: number;
  /** 退款中金额 */
  refundingAmount?: number;
  /** 分账冻结金额 */
  frozenAmount?: number;
}

export interface ICompanyBalance {
  /** 待结算金额 */
  unsettledAmount?: number;
  /** 待分账金额 */
  unallocatedAmount?: number;
  /** 可提现金额 */
  withdrawableAmount?: number;
  /** 退款金额 */
  refundAmount?: number;
  /** 退款中金额 */
  refundingAmount?: number;
  /** 分账中金额 */
  allocatingAmount?: number;
  /** 提现中金额 */
  withdrawingAmount?: number;
  /** 分账冻结金额 */
  frozenAmount?: number;
}

/** 易宝账户 - detail */
export interface IYibaoDetail extends Omit<IYibaoBaseInfo, 'companyBalance' | 'agentBalance'>, IYibaoLicense, IYibaoLeagalPerson, IElectronicLeagal, IYibaoOpening {
  /** 企业支付手机号 */
  payPhone?: string;
  /** 店铺信息 */
  shopList?: IYibaoShop[];
  /** 受益人信息 */
  uboList?: IBeneficiary[];
  /** 开户支行名称 */
  bankBranch?: string;
  /** 行业大类 */
  industryCategory?: string;
  /** 行业类别 */
  industrySubcategory?: string;
  /** 商户类别 */
  merchantCategory?: string;
  /** 商品类别码（开通pos产品用） */
  mcc?: string;
  /** 支付场景 */
  paymentScene?: string;
  /** 付款业务与上下游签署的协议 */
  agreementPhotoUrl?: string;
  /** 付款业务与上下游签署的协议 */
  agreementPhotoName?: string;
  /** 转账协议 */
  transferAgreementUrl?: string;
  /** 转账协议名称 */
  transferAgreementName?: string;
  /**
   * 分账场景
   * @description {@link EShareScene}
   */
  shareScene?: EShareScene;
  /** 分账证明资质文件1 */
  shareCertificate1?: string;
  /** 分账证明资质文件1 */
  shareCertificate1Name?: string;
  /** 分账证明资质文件2 */
  shareCertificate2?: string;
  /** 分账证明资质文件1 */
  shareCertificate2Name?: string;
  /** 分账证明资质文件3 */
  shareCertificate3?: string;
  /** 分账证明资质文件1 */
  shareCertificate3Name?: string;
  /** 分账证明资质文件4 */
  shareCertificate4?: string;
  /** 分账证明资质文件1 */
  shareCertificate4Name?: string;
  /** 分账证明资质文件5 */
  shareCertificate5?: string;
  /** 分账证明资质文件1 */
  shareCertificate5Name?: string;
  /** 描述 */
  shareCertificateRemark?: string;
  /** */
  bankElecAccounts?: IYibaobankElecAccounts[];
  /** */
  accountBooks?: IAccountBook[];
}

export interface IAccountBook {
  /** 商户编号 */
  merchantNo?: string;
  /** 商户侧记账薄编号 */
  merchantAccountBookNo?: string;
  /** 商户侧记账薄名称 */
  merchantAccountBookName?: string;
  /** 易宝记账薄编号 */
  ypAccountBookNo?: string;
  /** 状态 SUCCESS:成功 PROCESSING:处理中 FAIL:失败  */
  status?: EAccountBookStatus;
}

export interface IYibaobankElecAccounts{
  /** 银行编码 */
  bankCode?: string;
  /** 银行账户名 */
  bankAccountName?: string;
  /** 银行账户号 */
  bankAccountNo?: string;
}

export enum EShareScene {
  /** 司机/车队劳务费（网络货运平台） */
  FZ_WL001 = 'FZ_WL001',
  /** 司机/车队劳务费（交通运输） */
  FZ_WL002 = 'FZ_WL002',
  /** 供应商货款 */
  SupplierPayment = 'FZ_FJ001',
  /** 分销商/经销商/站点/加盟店应收账款 */
  FranchiseAccountsReceivable = 'FZ_FJ002',
  /** 推广返佣 */
  PromotionRebate = 'FZ_FJ003',
  /** 系统商/平台商服务费 */
  ProviderServiceFee = 'FZ_FJ004',
  /** 平台补贴 */
  PlatformSubsidy = 'FZ_FJ005',
  /** 货主应收货款 */
  ReceivableByOwner = 'FZ_FJ007',
  /** 资方应收账款 */
  AccountsReceivable = 'FZ_JR001',
  /** 助贷服务费/担保费等 */
  LoanServiceFee = 'FZ_JR002',
  /** 入驻商户应收账款 */
  SettledMerchantsReceivable = 'FZ_FJ006',
  /** 用户应收储值款 */
  DepositReceive = 'FZ_FJ008',
  /** 公司内部/集团/股东收入 */
  FZ_ALL001 = 'FZ_ALL001',
}

export const shareSceneLabel = {
  [EShareScene.FZ_WL001]: '司机/车队劳务费（网络货运平台）',
  [EShareScene.FZ_WL002]: '司机/车队劳务费（交通运输）',
  [EShareScene.SupplierPayment]: '供应商货款',
  [EShareScene.FranchiseAccountsReceivable]: '分销商/经销商/站点/加盟店应收账款',
  [EShareScene.PromotionRebate]: '推广返佣',
  [EShareScene.ProviderServiceFee]: '系统商/平台商服务费',
  [EShareScene.PlatformSubsidy]: '平台补贴',
  [EShareScene.SettledMerchantsReceivable]: '入驻商户应收账款',
  [EShareScene.ReceivableByOwner]: '货主应收货款',
  [EShareScene.DepositReceive]: '用户应收储值款',
  [EShareScene.FZ_ALL001]: '公司内部/集团/股东收入',
  [EShareScene.AccountsReceivable]: '资方应收账款',
  [EShareScene.LoanServiceFee]: '助贷服务费/担保费等',
};

export enum EPaymentScene {
  /** 人力资源服务 */
  RLZYFW = 'RLZYFW',
  /** 理赔/款项回退 */
  LPKXHT = 'LPKXHT',
  /** 退款 */
  TK = 'TK',
  /** 寄托售卖（二手） */
  JTSM = 'JTSM',
  /** 典当/抵押贷 */
  DDDY = 'DDDY',
  /** 打赏/会员福利 */
  DSHYFL = 'DSHYFL',
  /** 兑奖 */
  DJ = 'DJ',
  /** 租赁费 */
  ZLF = 'ZLF',
  /** 商户返佣 */
  SHFY = 'SHFY',
  /** 供应商打款 */
  GYSDK = 'GYSDK',
  /** 下级单位打款 */
  XJDWDK = 'XJDWDK',
  /** 其他 */
  QT = 'QT',
  /** 理财 */
  LC = 'LC',
  /** 借款人打款 */
  JKRDK = 'JKRDK',
  /** 消费贷 */
  XFD = 'XFD',
}

export const paymentSceneLabel = {
  [EPaymentScene.RLZYFW]: '人力资源服务',
  [EPaymentScene.LPKXHT]: '理赔/款项回退',
  [EPaymentScene.TK]: '退款',
  [EPaymentScene.JTSM]: '寄托售卖（二手）',
  [EPaymentScene.DDDY]: '典当/抵押贷',
  [EPaymentScene.DSHYFL]: '打赏/会员福利',
  [EPaymentScene.DJ]: '兑奖',
  [EPaymentScene.ZLF]: '租赁费',
  [EPaymentScene.SHFY]: '商户返佣',
  [EPaymentScene.GYSDK]: '供应商打款',
  [EPaymentScene.XJDWDK]: '下级单位打款',
  [EPaymentScene.QT]: '其他',
  [EPaymentScene.LC]: '理财',
  [EPaymentScene.JKRDK]: '借款人打款',
  [EPaymentScene.XFD]: '消费贷',
};

export enum EYibaoApplyStatus {
  /** 申请审核中 */
  REVIEWING = 'REVIEWING',
  /** 申请已驳回 */
  REVIEW_BACK = 'REVIEW_BACK',
  /** 真实性验证中 */
  AUTHENTICITY_VERIFYING = 'AUTHENTICITY_VERIFYING',
  /** 协议待签署 */
  AGREEMENT_SIGNING = 'AGREEMENT_SIGNING',
  /** 业务开通中 */
  BUSINESS_OPENING = 'BUSINESS_OPENING',
  /** 申请已完成 */
  COMPLETED = 'COMPLETED',
}
export const yibaoApplyStatusLabel = {
  [EYibaoApplyStatus.REVIEWING]: '申请审核中',
  [EYibaoApplyStatus.REVIEW_BACK]: '申请已驳回',
  [EYibaoApplyStatus.AUTHENTICITY_VERIFYING]: '真实性验证中',
  [EYibaoApplyStatus.AGREEMENT_SIGNING]: '协议待签署',
  [EYibaoApplyStatus.BUSINESS_OPENING]: '业务开通中',
  [EYibaoApplyStatus.COMPLETED]: '申请已完成',
};

export const yibaoApplyStatus = [
  {
    value: EYibaoApplyStatus.REVIEWING,
    label: yibaoApplyStatusLabel[EYibaoApplyStatus.REVIEWING],
  },
  {
    value: EYibaoApplyStatus.REVIEW_BACK,
    label: yibaoApplyStatusLabel[EYibaoApplyStatus.REVIEW_BACK],
  },
  {
    value: EYibaoApplyStatus.AUTHENTICITY_VERIFYING,
    label: yibaoApplyStatusLabel[EYibaoApplyStatus.AUTHENTICITY_VERIFYING],
  },
  {
    value: EYibaoApplyStatus.AGREEMENT_SIGNING,
    label: yibaoApplyStatusLabel[EYibaoApplyStatus.AGREEMENT_SIGNING],
  },
  {
    value: EYibaoApplyStatus.BUSINESS_OPENING,
    label: yibaoApplyStatusLabel[EYibaoApplyStatus.BUSINESS_OPENING],
  },
  {
    value: EYibaoApplyStatus.COMPLETED,
    label: yibaoApplyStatusLabel[EYibaoApplyStatus.COMPLETED],
  },
];

export enum EYibaoAccountStatus {
  /** 待申请 */
  DRAFT = 'DRAFT',
  /** 待提交 */
  PENDING_SUBMIT = 'PENDING_SUBMIT',
  /** 审核中 */
  REVIEWING = 'REVIEWING',
  /** 审核中（产品开通中） */
  BUSINESS_OPENING = 'BUSINESS_OPENING',
  /** 申请失败（审核不通过） */
  REVIEW_BACK = 'REVIEW_BACK',
  /** 待签署（协议） */
  AGREEMENT_SIGNING = 'AGREEMENT_SIGNING',
  /** 审核通过 */
  COMPLETED = 'COMPLETED',
}

export const yibaoAccountStatusLabel = {
  [EYibaoAccountStatus.DRAFT]: '待申请',
  [EYibaoAccountStatus.PENDING_SUBMIT]: '待提交',
  [EYibaoAccountStatus.REVIEWING]: '审核中',
  [EYibaoAccountStatus.BUSINESS_OPENING]: '审核中',
  [EYibaoAccountStatus.REVIEW_BACK]: '申请失败',
  [EYibaoAccountStatus.AGREEMENT_SIGNING]: '开通中',
  [EYibaoAccountStatus.COMPLETED]: '已开通',
};

export const yibaoAccountStatusStyle = {
  [EYibaoAccountStatus.DRAFT]: 'primary',
  [EYibaoAccountStatus.PENDING_SUBMIT]: 'primary',
  [EYibaoAccountStatus.REVIEWING]: 'primary',
  [EYibaoAccountStatus.BUSINESS_OPENING]: 'primary',
  [EYibaoAccountStatus.REVIEW_BACK]: 'danger',
  [EYibaoAccountStatus.AGREEMENT_SIGNING]: 'primary',
  [EYibaoAccountStatus.COMPLETED]: 'success',
};

export interface IYibaoOpeningProduct {
  /**
   * 产品类别
   * @description {@link EProductClass}
   */
  productClass?: EProductClass;
  /**
   * 申请状态
   * @description {@link EYibaoApplyStatus}
   */
  applicationStatus?: EYibaoApplyStatus;
  /** 审核意见 */
  auditOpinion?: string;
  /** 协议签署地址 */
  agreementSignUrl?: string;
}

export interface IYibaoCnapsCode {
  /** 支行名称 */
  cnapsCode: string;
  /** 支行名称 */
  cnapsName: string;
}

export interface IMasterBank {
  bankName: string;
  bankCode: string;
}

/** 易宝店铺业务类型 */
export enum EYibaoBusinessType {
  /** 抖音 */
  DOUYIN = 'DOUYIN',
  /** 美团 */
  MEITUAN = 'MEITUAN',
  /** 京东 */
  JINGDONG = 'JINGDONG',
  /** 拼多多 */
  PINDUODUO = 'PINDUODUO',
  /** 小红书 */
  XIAOHONGSHU = 'XIAOHONGSHU',
}

export const yibaoBussinessTypeName = {
  [EYibaoBusinessType.DOUYIN]: '抖音',
  [EYibaoBusinessType.MEITUAN]: '美团',
  [EYibaoBusinessType.JINGDONG]: '京东',
  [EYibaoBusinessType.PINDUODUO]: '拼多多',
  [EYibaoBusinessType.XIAOHONGSHU]: '小红书',
};

export const yibaoBussinessTypes = [
  {
    label: yibaoBussinessTypeName[EYibaoBusinessType.DOUYIN],
    value: EYibaoBusinessType.DOUYIN,
  },
  {
    label: yibaoBussinessTypeName[EYibaoBusinessType.MEITUAN],
    value: EYibaoBusinessType.MEITUAN,
  },
  {
    label: yibaoBussinessTypeName[EYibaoBusinessType.JINGDONG],
    value: EYibaoBusinessType.JINGDONG,
  },
  {
    label: yibaoBussinessTypeName[EYibaoBusinessType.PINDUODUO],
    value: EYibaoBusinessType.PINDUODUO,
  },
  {
    label: yibaoBussinessTypeName[EYibaoBusinessType.XIAOHONGSHU],
    value: EYibaoBusinessType.XIAOHONGSHU,
  },
];

type ISQ = Pick<
  IYibaoDetail,
  | 'id'
  | 'agreementPhotoUrl'
  | 'agreementPhotoName'
  | 'industryCategory'
  | 'industrySubcategory'
  | 'merchantCategory'
  | 'mcc'
  | 'paymentScene'
  | 'shareScene'
  | 'shareCertificate1'
  | 'shareCertificate1Name'
  | 'shareCertificate2'
  | 'shareCertificate2Name'
  | 'shareCertificate3'
  | 'shareCertificate3Name'
  | 'shareCertificate4'
  | 'shareCertificate4Name'
  | 'shareCertificate5'
  | 'shareCertificate5Name'
>

export interface ISceneQualificationParam extends Partial<ISQ> {}

export interface IQualificationParam {
  /** 易宝id */
  id?: number;
  /** 转账协议 */
  transferAgreementUrl?: string;
  /** 转账协议名称 */
  transferAgreementName?: string;
}

export interface IYibaoWithdraw {
  /** 账户连连id */
  id?: number;
  /** 绑定银行卡主键 */
  bankAccountId?: number;
  /** 提现金额 */
  amount?: number;
  /** 验证码 */
  verifyCode?: string;
  /** 支付密码 */
  payPassword?: string;
  /** 手机号码 */
  phoneNo?: string;
  /** 可提现金额 */
  withdrawableAmount?: number;
}

export interface IYibaoPayee {
  id?: number;
  /** 通道类型 */
  channelType: EPaymentChannel;
  /**
   * 收款方类型
   * @description 1:个人 2:企业
   */
  payeeType: '1' | '2';
  /** 个人姓名/企业名称 */
  payeeName: string;
  /** 身份证/统一社会信用代码 */
  payeeIdentity: string;
  /** 手机号 */
  phone?: string;
  /**
   * 银行账户类型
   * @description {@link EBankAccountType}
   */
  bankAccountType?: EBankAccountType;
  /** 银行账号（企业基本账号） */
  bankCardNo?: string;
  /** 开户银行code */
  bankCode?: string;
  /** 开户省份code */
  provinceCode?: string;
  /** 开户城市code */
  cityCode?: string;
  /** 手动填写的开户支行 - 大额行号 */
  cnapsCode?: string;
  /**
   * 开户支行是否手动填写： 1-是，0-否
   */
  customerStatus?: 1 | 0;

  /** 开户银行名称 (仅用于前端展示) */
  brabankName?: string;
  /** 手动填写的开户支行name */
  customBankBranchName?: string;
  /** 自动匹配的开户支行行号(其实就是cnapsCode)  */
  bankBranchNo?: string;
  /** 开户支行名称(手动填写) */
  relationBranchBankName?: string;
  /** 开户支行名称 */
  bankBranch?: string;
  /** 开户银行名称(接口) */
  bankName?: string;
  /** 企业ID */
  companyId?: number;
  /** 集团创建收款方，是否自动同步到子企业。默认：false */
  autoCreateInSubCompany?: boolean; 
}

export enum EAccountBookStatus {
  /** 未申请 */
  NOTAPPLY = undefined,
  /** 成功 */
  SUCCESS = 'SUCCESS',
  /** 处理中 */
  PROCESSING = 'PROCESSING',
  /** 失败 */
  FAIL = 'FAIL',
}

export const accountBookStatusLabel = {
  [EAccountBookStatus.NOTAPPLY]: '未申请',
  [EAccountBookStatus.SUCCESS]: '成功',
  [EAccountBookStatus.PROCESSING]: '处理中',
  [EAccountBookStatus.FAIL]: '失败',
};

export const accountBookStatusStyle = {
  [EAccountBookStatus.NOTAPPLY]: 'info',
  [EAccountBookStatus.SUCCESS]: 'success',
  [EAccountBookStatus.PROCESSING]: 'primary',
  [EAccountBookStatus.FAIL]: 'danger',
};

export interface IYopAccount {
  /** 企业id */
  companyId?: number;
  /** 企业编码 */
  companyCode?: string;
  /** 企业名称 */
  companyName?: string;
  /** 代理商id */
  agentId?: number;
  /** 代理商编码 */
  agentCode?: string;
  /** 代理商名称 */
  agentName?: string;
  /**
   * 代理商等级
   * @description {@link EAgentLevel}
   */
  agentLevel?: EAgentLevel;
  /** 商户id */
  merchantId?: number;
  /** 易宝侧商户编号 */
  merchantNo?: string;
  /** 待结算金额 */
  unsettledAmount?: number;
  /** 待分账金额 */
  unallocatedAmount?: number;
  /** 可提现金额 */
  withdrawableAmount?: number;
  /** 退款金额 */
  refundAmount?: number;
  /**
   * 通道状态
   * @description {@link EChannelStatus}
   */
  channelStatus?: EChannelStatus;
  /**
   * 易宝账户状态
   * @description {@link EYopAccountStatus}
   */
  merchantStatus?: EYopAccountStatus;

  /**
   * 通道产品费率
   * @description {@link IProductFee}
   */
  productFees?: IProductFee[];
}

export interface IYopAccountsParam extends IPageParam {
  /** 企业id */
  companyId?: number;
  /** 企业编码 */
  companyCode?: string;
  /** 代理商id */
  agentId?: number;
  /** 代理商编码 */
  agentCode?: string;
  /**
   * 账户状态
   * @description {@link EYopAccountStatus}
   */
  merchantStatus?: EYopAccountStatus;
}

export enum EYopAccountStatus {
  /** 待提交 */
  PENDING_SUBMIT = 'PENDING_SUBMIT',
  /** 审核中 */
  REVIEWING = 'REVIEWING',
  /** 审核驳回 */
  REVIEW_BACK = 'REVIEW_BACK',
  /** 协议签署中 */
  AGREEMENT_SIGNING = 'AGREEMENT_SIGNING',
  /** 业务开通中 */
  BUSINESS_OPENING = 'BUSINESS_OPENING',
  /** 入网成功 */
  COMPLETED = 'COMPLETED',
}

export const yopAccountStatusLabel = {
  [EYopAccountStatus.PENDING_SUBMIT]: '待提交',
  [EYopAccountStatus.REVIEWING]: '审核中',
  [EYopAccountStatus.REVIEW_BACK]: '审核驳回',
  [EYopAccountStatus.AGREEMENT_SIGNING]: '签署中',
  [EYopAccountStatus.BUSINESS_OPENING]: '开通中',
  [EYopAccountStatus.COMPLETED]: '入网成功',
};

export const yopAccountStatusStyle = {
  [EYopAccountStatus.PENDING_SUBMIT]: 'primary',
  [EYopAccountStatus.REVIEWING]: 'primary',
  [EYopAccountStatus.REVIEW_BACK]: 'danger',
  [EYopAccountStatus.AGREEMENT_SIGNING]: 'primary',
  [EYopAccountStatus.BUSINESS_OPENING]: 'primary',
  [EYopAccountStatus.COMPLETED]: 'success',
};

export const yopAccountStatusList = [
  {
    label: yopAccountStatusLabel[EYopAccountStatus.PENDING_SUBMIT],
    value: EYopAccountStatus.PENDING_SUBMIT,
  },
  {
    label: yopAccountStatusLabel[EYopAccountStatus.REVIEWING],
    value: EYopAccountStatus.REVIEWING,
  },
  {
    label: yopAccountStatusLabel[EYopAccountStatus.REVIEW_BACK],
    value: EYopAccountStatus.REVIEW_BACK,
  },
  {
    label: yopAccountStatusLabel[EYopAccountStatus.AGREEMENT_SIGNING],
    value: EYopAccountStatus.AGREEMENT_SIGNING,
  },
  {
    label: yopAccountStatusLabel[EYopAccountStatus.BUSINESS_OPENING],
    value: EYopAccountStatus.BUSINESS_OPENING,
  },
  {
    label: yopAccountStatusLabel[EYopAccountStatus.COMPLETED],
    value: EYopAccountStatus.COMPLETED,
  },
];

export interface IYopCompanyList {
  /** 企业id */
  companyId?: number;
  /** 企业编码 */
  companyCode?: string;
  /** 企业名称 */
  companyName?: string;
  /** 集团企业类型(0非集团企业 1集团企业) */
  groupType?: number;
}